import { Badge } from "@bcc/components/ui/badge";
import { Card, CardContent } from "@bcc/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Calendar, Package, Truck, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setBatchDetailModalTestId } from "./batch-detail-modal.utils";

interface BatchDetailModalProps {
  batchId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId?: string;
}

const batchDetails = {
  "BATCH-001": {
    id: "BATCH-001",
    created: "2025-01-06",
    carrier: "USPS",
    totalOrders: 3,
    totalItems: 8,
    status: "shipped",
    orders: [
      { id: "ORD-2847", customer: "Sarah Johnson", items: 2 },
      { id: "ORD-2845", customer: "Emma Davis", items: 3 },
      { id: "ORD-2841", customer: "Sophia Lee", items: 3 },
    ],
  },
  "BATCH-002": {
    id: "BATCH-002",
    created: "2025-01-05",
    carrier: "UPS",
    totalOrders: 3,
    totalItems: 11,
    status: "completed",
    orders: [
      { id: "ORD-2844", customer: "James Wilson", items: 1 },
      { id: "ORD-2842", customer: "David Brown", items: 6 },
      { id: "ORD-2840", customer: "Liam Taylor", items: 2 },
    ],
  },
};

export function BatchDetailModal({
  batchId,
  open,
  onOpenChange,
  testId,
}: BatchDetailModalProps) {
  const { t } = useTranslation();

  if (!batchId) return null;

  const batch = batchDetails[batchId as keyof typeof batchDetails];
  if (!batch) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        testId={testId ?? setBatchDetailModalTestId("content")}
        className="max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("views.batchDetailModal.title")}: {batch.id}
          </DialogTitle>
          <DialogDescription>
            {t("views.batchDetailModal.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Batch Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("views.batchDetailModal.created")}
                    </p>
                    <p className="text-lg font-semibold">{batch.created}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Truck className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("views.batchDetailModal.carrier")}
                    </p>
                    <p className="text-lg font-semibold">{batch.carrier}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("views.batchDetailModal.totalOrders")}
                    </p>
                    <p className="text-lg font-semibold">{batch.totalOrders}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Package className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t("views.batchDetailModal.totalItems")}
                    </p>
                    <p className="text-lg font-semibold">
                      {batch.totalItems} {t("views.batchDetailModal.bags")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Orders List */}
          <div>
            <h3 className="text-sm font-semibold mb-3">
              {t("views.batchDetailModal.ordersInBatch")}
            </h3>
            <div className="space-y-2">
              {batch.orders.map((order) => (
                <Card key={order.id}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-mono text-sm font-medium">
                            {order.id}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {order.customer}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">
                        {order.items}{" "}
                        {order.items === 1
                          ? t("views.batchDetailModal.item")
                          : t("views.batchDetailModal.items")}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
